'use strict';

/**
 * thermalPrinter.js
 *
 * Bridge para impresoras térmicas BLE (58mm ESC/POS genéricas).
 *
 * Flujo:
 *   1. thermalPrinter.init()         — solicita permisos BLE
 *   2. thermalPrinter.scan(cb)       — escanea / abre picker de dispositivos
 *   3. thermalPrinter.connect(id)    — conecta y detecta characteristic de escritura
 *   4. thermalPrinter.printReceipt() — genera bytes ESC/POS y los envía
 *   5. thermalPrinter.disconnect()   — cierra la conexión
 *
 * En app nativa (Capacitor) usa @capacitor-community/bluetooth-le.
 * En PWA/navegador usa la Web Bluetooth API (Chrome Android ≥ 56).
 */

import { BleClient } from '@capacitor-community/bluetooth-le';
import { Capacitor } from '@capacitor/core';

const isNative  = Capacitor.isNativePlatform();
const hasWebBT  = !isNative && 'bluetooth' in navigator;
const hasWebUSB = !isNative && 'usb' in navigator;

// ─── Estado interno ───────────────────────────────────────────────────────────

// Capacitor BLE
let _deviceId   = null;
let _serviceId  = null;
let _charId     = null;

// Web Bluetooth
let _webDevice  = null;
let _webChar    = null;
// Almacena BluetoothDevice por id para que connect(id) pueda recuperarlo
const _webDeviceMap = new Map();

// WebUSB
let _usbDevice   = null;
let _usbIface    = null;
let _usbEndpoint = null;

// Clase USB de impresora — la preferimos al buscar el endpoint de escritura
const USB_CLASS_PRINTER = 0x07;

/**
 * Los USBDevice no exponen un id estable como los BluetoothDevice.
 * Componemos uno con vendor/product/serial para poder re-encontrarlo
 * en navigator.usb.getDevices() tras recargar.
 */
const usbKey = (d) =>
  `usb:${d.vendorId}:${d.productId}:${d.serialNumber ?? ''}`;

// UUIDs de servicios comunes en impresoras ESC/POS Bluetooth
const PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb', // ESC/POS genérico
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // ISSC Transparent
  '0000ff00-0000-1000-8000-00805f9b34fb', // Serial genérico FF00
  '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 / JDY
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Xprinter
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const strip = (str = '') =>
  str.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();

const encoder = new TextEncoder();

// Genera el bloque de bytes ESC/POS para el recibo
function buildReceipt({ business, date, cashier, customer, folio, items = [], subtotal, discount, total, method, changeDue, note }) {
  const chunks = [];

  const push = (str) => chunks.push(encoder.encode(str));
  const cmd  = (...bytes) => chunks.push(new Uint8Array(bytes));

  const LINE  = '-'.repeat(32) + '\n';
  const DLINE = '='.repeat(32) + '\n';

  const padLine = (left, right) => {
    const gap = 32 - left.length - right.length;
    return left + ' '.repeat(Math.max(gap, 1)) + right + '\n';
  };

  const METHOD_LABEL = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', mixed: 'Mixto' };
  const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  // ESC @ — initialize
  cmd(0x1B, 0x40);

  // ── Header centrado ──
  cmd(0x1B, 0x61, 0x01); // center
  cmd(0x1D, 0x21, 0x11); // double size
  push(strip(business?.name ?? 'GEEK COMMERCE') + '\n');
  cmd(0x1D, 0x21, 0x00); // normal size
  if (business?.rfc)     push('RFC: ' + business.rfc + '\n');
  if (business?.address) push(strip(business.address) + '\n');
  if (business?.phone)   push('Tel: ' + business.phone + '\n');

  // ── Metadata ──
  cmd(0x1B, 0x61, 0x00); // left
  push(LINE);
  push(date + '\n');
  if (cashier)  push('Cajero:  ' + strip(cashier)  + '\n');
  if (customer) push('Cliente: ' + strip(customer) + '\n');
  push('Folio: ' + folio + '\n');
  push(LINE);

  // ── Ítems ──
  for (const item of items) {
    const name        = strip(item.name).slice(0, 22);
    const lineTotal   = fmt(item.quantity * item.unit_price);
    const origPrice   = item.original_price ?? null;
    const hasDiscount = origPrice && origPrice > item.unit_price;

    if (item.quantity > 1) {
      push(`  ${item.quantity}x ${strip(item.name)}\n`);
      if (hasDiscount) push(padLine('  Precio lista', fmt(origPrice)));
      push(padLine('', lineTotal));
    } else {
      push(padLine('  ' + name, lineTotal));
      if (hasDiscount) push(padLine('  Precio lista:', fmt(origPrice)));
    }
  }

  push(LINE);

  // ── Totales ──
  if (discount > 0) {
    push(padLine('Subtotal', fmt(subtotal)));
    push(padLine('Descuento', '-' + fmt(discount)));
    push(DLINE);
  }

  cmd(0x1D, 0x21, 0x01); // double height for total
  push(padLine('TOTAL', fmt(total)));
  cmd(0x1D, 0x21, 0x00);

  push(padLine(METHOD_LABEL[method] ?? method, ''));
  if (method === 'cash' && changeDue > 0) {
    push(padLine('Cambio', fmt(changeDue)));
  }

  // ── Nota ──
  if (note) {
    push(LINE);
    push('Nota: ' + strip(note) + '\n');
  }

  // ── Footer ──
  cmd(0x1B, 0x61, 0x01); // center
  push('\n');
  push('Este comprobante no es CFDI.\n');
  push('Solicita tu factura al\n');
  push('establecimiento.\n');
  push('Gracias por su compra!\n');
  push('\n\n\n');

  // GS V 66 3 — partial cut con avance de 3 líneas
  cmd(0x1D, 0x56, 0x42, 0x03);

  // Concatenar todos los chunks en un solo Uint8Array
  const total_len = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const result    = new Uint8Array(total_len);
  let offset = 0;
  for (const c of chunks) {
    result.set(c, offset);
    offset += c.byteLength;
  }
  return result;
}

// Genera el bloque de bytes ESC/POS para el corte de caja
function buildCorte({ business, cashier, terminal, openedAt, closedAt, salesCount, salesTotal, openingCash, closingCash, expectedCash, breakdown, diff, notes }) {
  const chunks = [];
  const push   = (str) => chunks.push(encoder.encode(str));
  const cmd    = (...bytes) => chunks.push(new Uint8Array(bytes));

  const LINE  = '-'.repeat(32) + '\n';
  const DLINE = '='.repeat(32) + '\n';
  const padLine = (left, right) => {
    const gap = 32 - left.length - right.length;
    return left + ' '.repeat(Math.max(gap, 1)) + right + '\n';
  };
  const fmt = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  cmd(0x1B, 0x40); // init
  cmd(0x1B, 0x61, 0x01); // center
  cmd(0x1D, 0x21, 0x11); // double size
  push(strip(business?.name ?? 'GEEK COMMERCE') + '\n');
  cmd(0x1D, 0x21, 0x00);
  if (business?.rfc) push('RFC: ' + business.rfc + '\n');
  push(LINE);
  cmd(0x1D, 0x21, 0x01);
  push('CORTE DE CAJA\n');
  cmd(0x1D, 0x21, 0x00);
  push('\n');

  cmd(0x1B, 0x61, 0x00); // left
  if (terminal)  push('Terminal: ' + strip(terminal) + '\n');
  if (cashier)   push('Cajero:   ' + strip(cashier)  + '\n');
  if (openedAt)  push('Apertura: ' + strip(openedAt) + '\n');
  if (closedAt)  push('Cierre:   ' + strip(closedAt) + '\n');
  push(LINE);

  push(padLine('Ventas', String(salesCount ?? 0)));
  cmd(0x1D, 0x21, 0x01);
  push(padLine('TOTAL VENDIDO', fmt(salesTotal)));
  cmd(0x1D, 0x21, 0x00);
  push(LINE);

  if (breakdown) {
    if ((breakdown.cash      ?? 0) > 0) push(padLine('  Efectivo',      fmt(breakdown.cash)));
    if ((breakdown.card      ?? 0) > 0) push(padLine('  Tarjeta',       fmt(breakdown.card)));
    if ((breakdown.transfer  ?? 0) > 0) push(padLine('  Transferencia', fmt(breakdown.transfer)));
    push(LINE);
  }

  push(padLine('Efectivo inicial', fmt(openingCash)));
  push(padLine('Esperado en caja', fmt(expectedCash)));
  if (closingCash != null) {
    push(padLine('Contado al cierre', fmt(closingCash)));
    push(DLINE);
    if (diff === 0)      push(padLine('Sin diferencia', ''));
    else if (diff > 0)   push(padLine('Sobrante', '+' + fmt(diff)));
    else                 push(padLine('FALTANTE', fmt(diff)));
  }

  if (notes) {
    push(LINE);
    push('Notas: ' + strip(notes) + '\n');
  }

  cmd(0x1B, 0x61, 0x01);
  push('\n\n\n');
  cmd(0x1D, 0x56, 0x42, 0x03); // partial cut

  const total_len = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const result    = new Uint8Array(total_len);
  let offset = 0;
  for (const c of chunks) { result.set(c, offset); offset += c.byteLength; }
  return result;
}

// Divide el buffer en chunks para BLE (MTU típico 512 bytes)
async function writeChunked(deviceId, serviceId, charId, data) {
  const MTU    = 512;
  let   offset = 0;
  while (offset < data.byteLength) {
    const slice = data.slice(offset, offset + MTU);
    await BleClient.writeWithoutResponse(deviceId, serviceId, charId, new DataView(slice.buffer));
    offset += MTU;
    await new Promise((r) => setTimeout(r, 30));
  }
}

async function writeChunkedWeb(char, data) {
  const MTU    = 512;
  let   offset = 0;
  while (offset < data.byteLength) {
    const slice = data.slice(offset, offset + MTU);
    try {
      await char.writeValueWithoutResponse(slice);
    } catch {
      await char.writeValue(slice);
    }
    offset += MTU;
    await new Promise((r) => setTimeout(r, 30));
  }
}

// USB bulk soporta transferencias grandes, pero troceamos igual para
// no desbordar el buffer del firmware en impresoras baratas.
async function writeChunkedUsb(device, endpoint, data) {
  const CHUNK  = 4096;
  let   offset = 0;
  while (offset < data.byteLength) {
    await device.transferOut(endpoint, data.slice(offset, offset + CHUNK));
    offset += CHUNK;
  }
}

/**
 * Abre el USBDevice y reclama la interfaz con un endpoint bulk OUT.
 * Prefiere la interfaz de clase impresora (0x07); si no existe, toma
 * la primera que tenga un bulk OUT — muchas térmicas se presentan como
 * vendor-specific (0xFF).
 */
async function usbOpen(device) {
  await device.open();
  if (!device.configuration) await device.selectConfiguration(1);

  const candidates = [];
  for (const iface of device.configuration.interfaces) {
    for (const alt of iface.alternates) {
      const ep = alt.endpoints.find((e) => e.direction === 'out' && e.type === 'bulk');
      if (ep) {
        candidates.push({
          ifaceNum:   iface.interfaceNumber,
          altSetting: alt.alternateSetting,
          endpoint:   ep.endpointNumber,
          isPrinter:  alt.interfaceClass === USB_CLASS_PRINTER,
        });
      }
    }
  }

  const pick = candidates.find((c) => c.isPrinter) ?? candidates[0];
  if (!pick) {
    await device.close().catch(() => {});
    throw new Error('La impresora no expone un endpoint USB de escritura');
  }

  await device.claimInterface(pick.ifaceNum);
  if (pick.altSetting !== 0) {
    await device.selectAlternateInterface(pick.ifaceNum, pick.altSetting);
  }

  _usbDevice   = device;
  _usbIface    = pick.ifaceNum;
  _usbEndpoint = pick.endpoint;
  return { deviceId: usbKey(device), endpoint: pick.endpoint };
}

// ─── API pública ──────────────────────────────────────────────────────────────

export const thermalPrinter = {

  async init() {
    if (isNative) {
      await BleClient.initialize({ androidNeverForLocation: true });
    }
    // Web Bluetooth no requiere init — el permiso se solicita en requestDevice()
  },

  async scan(onDevice, timeoutMs = 8000) {
    if (isNative) {
      await BleClient.requestLEScan({}, (result) => {
        if (result.device?.deviceId) onDevice(result.device);
      });
      setTimeout(() => BleClient.stopLEScan().catch(() => {}), timeoutMs);
      return;
    }

    if (!hasWebBT) return;

    // Web Bluetooth: abre el selector de dispositivos del navegador
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: PRINTER_SERVICES,
    });

    _webDeviceMap.set(device.id, device);
    onDevice({ deviceId: device.id, name: device.name ?? 'Impresora BT' });
  },

  async stopScan() {
    if (isNative) {
      await BleClient.stopLEScan().catch(() => {});
    }
    // Web Bluetooth: no hay escaneo continuo que detener
  },

  /** Abre el selector USB del navegador. Requiere gesto del usuario. */
  async scanUsb(onDevice) {
    if (!hasWebUSB) throw new Error('WebUSB no disponible en este navegador');

    const device = await navigator.usb.requestDevice({ filters: [] });
    onDevice({
      deviceId: usbKey(device),
      name: device.productName ?? `USB ${device.vendorId.toString(16)}`,
      transport: 'usb',
    });
  },

  async connectUsb(deviceId) {
    if (!hasWebUSB) throw new Error('WebUSB no disponible en este navegador');

    const devices = await navigator.usb.getDevices();
    const device  = devices.find((d) => usbKey(d) === deviceId);
    if (!device) throw new Error('Dispositivo USB no encontrado — conéctalo de nuevo');

    return usbOpen(device);
  },

  /**
   * Reconecta en silencio tras recargar la app, sin volver a pedir el
   * selector. Devuelve el transporte restaurado, o null si no se pudo.
   *
   * USB conserva el permiso entre recargas (navigator.usb.getDevices()).
   * Web Bluetooth solo lo conserva si el navegador expone getDevices(),
   * que en Chrome sigue detrás de un flag — por eso puede devolver null.
   */
  async autoReconnect(savedDeviceId, transport) {
    if (isNative || !savedDeviceId) return null;

    if (transport === 'usb' && hasWebUSB) {
      try {
        const devices = await navigator.usb.getDevices();
        const device  = devices.find((d) => usbKey(d) === savedDeviceId);
        if (!device) return null;
        await usbOpen(device);
        return 'usb';
      } catch {
        return null;
      }
    }

    if (transport === 'ble' && hasWebBT && navigator.bluetooth.getDevices) {
      try {
        const devices = await navigator.bluetooth.getDevices();
        const device  = devices.find((d) => d.id === savedDeviceId);
        if (!device) return null;
        _webDeviceMap.set(device.id, device);
        await this.connect(device.id);
        return 'ble';
      } catch {
        return null;
      }
    }

    return null;
  },

  async connect(deviceId) {
    if (isNative) {
      await BleClient.connect(deviceId, () => {
        _deviceId = _serviceId = _charId = null;
      });

      const services = await BleClient.getServices(deviceId);
      for (const svc of services) {
        for (const ch of svc.characteristics) {
          if (ch.properties.writeWithoutResponse || ch.properties.write) {
            _deviceId  = deviceId;
            _serviceId = svc.uuid;
            _charId    = ch.uuid;
            return { serviceId: svc.uuid, charId: ch.uuid };
          }
        }
      }

      await BleClient.disconnect(deviceId).catch(() => {});
      throw new Error('No se encontró un characteristic de escritura en la impresora');
    }

    // Web Bluetooth
    const device = _webDeviceMap.get(deviceId);
    if (!device) throw new Error('Dispositivo no encontrado — escanea de nuevo');

    device.addEventListener('gattserverdisconnected', () => {
      _webDevice = null;
      _webChar   = null;
    });

    const server = await device.gatt.connect();

    // Muchas impresoras BLE baratas no responden a getPrimaryServices() sin UUID.
    // Primero probamos cada UUID conocido individualmente; si ninguno funciona,
    // intentamos el listado genérico como fallback.
    let foundChar = null;

    for (const serviceUuid of PRINTER_SERVICES) {
      try {
        const svc   = await server.getPrimaryService(serviceUuid);
        const chars = await svc.getCharacteristics();
        for (const ch of chars) {
          if (ch.properties.writeWithoutResponse || ch.properties.write) {
            foundChar = ch;
            break;
          }
        }
        if (foundChar) break;
      } catch {
        // Este UUID no existe en esta impresora — continuar
      }
    }

    if (!foundChar) {
      try {
        const services = await server.getPrimaryServices();
        for (const svc of services) {
          const chars = await svc.getCharacteristics();
          for (const ch of chars) {
            if (ch.properties.writeWithoutResponse || ch.properties.write) {
              foundChar = ch;
              break;
            }
          }
          if (foundChar) break;
        }
      } catch {}
    }

    if (!foundChar) {
      device.gatt.disconnect().catch(() => {});
      throw new Error('No se encontró un characteristic de escritura en la impresora');
    }

    _webDevice = device;
    _webChar   = foundChar;
    return { serviceId: foundChar.service.uuid, charId: foundChar.uuid };
  },

  async disconnect() {
    if (isNative) {
      if (!_deviceId) return;
      await BleClient.disconnect(_deviceId).catch(() => {});
      _deviceId = _serviceId = _charId = null;
      return;
    }

    if (_webDevice?.gatt?.connected) {
      _webDevice.gatt.disconnect();
    }
    _webDevice = null;
    _webChar   = null;

    if (_usbDevice) {
      try {
        if (_usbIface != null) await _usbDevice.releaseInterface(_usbIface);
        await _usbDevice.close();
      } catch {}
    }
    _usbDevice = _usbIface = _usbEndpoint = null;
  },

  isConnected() {
    if (isNative) return !!_deviceId;
    if (_usbDevice?.opened) return true;
    return _webDevice?.gatt?.connected ?? false;
  },

  /** Transporte activo: 'native' | 'usb' | 'ble' | null */
  activeTransport() {
    if (isNative) return _deviceId ? 'native' : null;
    if (_usbDevice?.opened) return 'usb';
    if (_webDevice?.gatt?.connected) return 'ble';
    return null;
  },

  capabilities() {
    return { isNative, hasWebBT, hasWebUSB };
  },

  async printReceipt(data) {
    await this._send(buildReceipt(data));
  },

  async printCorte(data) {
    await this._send(buildCorte(data));
  },

  /** Enruta los bytes ESC/POS por el transporte activo. */
  async _send(bytes) {
    if (isNative) {
      if (!_deviceId || !_serviceId || !_charId) throw new Error('Impresora no conectada');
      await writeChunked(_deviceId, _serviceId, _charId, bytes);
      return;
    }

    // USB primero: es más rápido y sobrevive recargas
    if (_usbDevice?.opened && _usbEndpoint != null) {
      await writeChunkedUsb(_usbDevice, _usbEndpoint, bytes);
      return;
    }

    if (_webChar && _webDevice?.gatt?.connected) {
      await writeChunkedWeb(_webChar, bytes);
      return;
    }

    // Sin impresora conectada — diálogo de impresión del sistema
    window.print();
  },
};
